import React, { useState } from "react";
import Lottie from "lottie-react";
import Modal from "../../../shared/components/Modal";
import strings from "../../../utils/Strings";
import { useAuthContext } from "../../auth/data/context/AuthContext";
import JobSearchClient from "../data/api/JobSearchClient";
import "./styles/JobSearchStyles.css";
import JobSearchLoader from "./JobSearchLoader";

interface NewJobSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (searchId?: string) => void;
}

const NewJobSearchModal: React.FC<NewJobSearchModalProps> = ({ isOpen, onClose, onSuccess }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { state: authState } = useAuthContext();
  const [title, setTitle] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [topK, setTopK] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progressPercent, setProgressPercent] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");

  const pollStatus = async (searchId: string) => {
    try {
      const statusRes = await JobSearchClient.getJobSearchStatus(searchId);
      const currentStatus = statusRes.status?.toUpperCase();
      if (currentStatus === "COMPLETE" || currentStatus === "COMPLETED") {
        setProgressPercent(100);
        setProgressMessage("Finalizing Results...");
        setTimeout(() => onSuccess(searchId), 1500);
      } else if (currentStatus === "FAILED") {
        setError(statusRes.error || "Talent search failed during processing.");
        setLoading(false);
      } else {
        // still processing, update progress
        setProgressPercent(statusRes.progress_percent || 0);
        setProgressMessage(statusRes.progress_message || "");
        // check again in 2 seconds
        setTimeout(() => pollStatus(searchId), 2000);
      }
    } catch (err: any) {
      setError(err.message || "Failed to check job search status");
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !jobDescription.trim()) {
      setError("Please fill out all fields.");
      return;
    }

    const validTopK = Math.min(100, Math.max(10, topK || 50));

    setLoading(true);
    setError(null);
    try {
      const result = await JobSearchClient.createJobSearch({
        title: title.trim(),
        job_description: jobDescription.trim(),
        top_k: validTopK,
        generate_summary: true,
      });
      
      const currentStatus = result.status?.toUpperCase();
      if (currentStatus === "PROCESSING") {
        setTimeout(() => pollStatus(result.search_id), 2000);
      } else if (currentStatus === "COMPLETE" || currentStatus === "COMPLETED") {
        onSuccess(result.search_id);
      } else {
        setError(result.error || "Talent search failed.");
        setLoading(false);
      }
    } catch (err: any) {
        setError(err.message || "Failed to create talent search");
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal open={isOpen} title={loading ? "" : "Talent Search"} onClose={loading ? () => {} : () => { setError(null); onClose(); }} width={loading ? "560px" : "480px"}>
{loading ? (
        <JobSearchLoader progressPercent={progressPercent} progressMessage={progressMessage} />
      ) : (
        <form onSubmit={handleSubmit} className="new-job-search-form">
          {error && <div className="form-error">{error}</div>}

          <div className="form-group" style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text-heading)' }}>
              {strings.jobSearch.title}
            </label>
            <input
              type="text"
              className="form-control"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Senior Frontend Developer"
              style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', transition: 'border-color 0.2s', outline: 'none' }}
              onFocus={(e) => e.target.style.borderColor = 'var(--primary)'}
              onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text-heading)' }}>
              {strings.jobSearch.jobDescription}
            </label>
            <textarea
              className="form-control"
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              placeholder="Enter the complete job description here... (Press Enter to search, Shift+Enter for new line)"
              rows={5}
              style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', lineHeight: '1.6', resize: "none", fontFamily: 'inherit', transition: 'border-color 0.2s', outline: 'none', overflowY: 'auto', maxHeight: '120px' }}
              onFocus={(e) => e.target.style.borderColor = 'var(--primary)'}
              onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
            />
          </div>

          <div className="form-group" style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text-heading)' }}>
              {strings.jobSearch.topK}
            </label>
            <input
              type="number"
              className="form-control"
              value={topK}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                if (!isNaN(val)) setTopK(Math.min(100, Math.max(10, val)));
              }}
              min={10}
              max={100}
              placeholder="50"
              style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', transition: 'border-color 0.2s', outline: 'none' }}
              onFocus={(e) => e.target.style.borderColor = 'var(--primary)'}
              onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
            />
            <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '4px', display: 'block' }}>
              {strings.jobSearch.topKHint}
            </span>
          </div>

          <div className="form-actions" style={{ marginTop: '20px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
            <button type="submit" className="btn-primary" disabled={loading} style={{ width: '100%', padding: '14px', borderRadius: '8px', fontSize: '1rem', fontWeight: 700, display: 'flex', justifyContent: 'center' }}>
              Talent Search
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default NewJobSearchModal;
